import { useReducer, useRef } from "react";
import type { ApiError, CheckRequest, CheckResponse, DesiredTone } from "../types/api";

type CheckStatus = "idle" | "verifying_turnstile" | "submitting" | "success" | "error";

type State = {
  status: CheckStatus;
  response: CheckResponse | null;
  error: ApiError["error"] | null;
};

type Action =
  | { type: "start_verify" }
  | { type: "start_submit" }
  | { type: "success"; response: CheckResponse }
  | { type: "error"; error: ApiError["error"] }
  | { type: "reset" };

const initialState: State = {
  status: "idle",
  response: null,
  error: null
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "start_verify":
      return { ...state, status: "verifying_turnstile", error: null };
    case "start_submit":
      return { ...state, status: "submitting", error: null };
    case "success":
      return { status: "success", response: action.response, error: null };
    case "error":
      return { ...state, status: "error", error: action.error };
    case "reset":
      return initialState;
    default:
      return state;
  }
}

async function parseApiResponse(response: Response): Promise<CheckResponse> {
  const payload = (await response.json()) as CheckResponse | ApiError;
  if (!response.ok) {
    throw payload;
  }
  return payload as CheckResponse;
}

export function useCheck() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortRef = useRef<AbortController | null>(null);
  const requestCounter = useRef(0);
  const originalRequestRef = useRef<Omit<CheckRequest, "turnstile_token"> | null>(null);

  async function submit(
    request: Omit<CheckRequest, "turnstile_token">,
    getTurnstileToken: () => Promise<string>
  ): Promise<void> {
    const requestId = requestCounter.current + 1;
    requestCounter.current = requestId;
    originalRequestRef.current = request;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      dispatch({ type: "start_verify" });
      const turnstileToken = await getTurnstileToken();
      if (controller.signal.aborted) return;
      dispatch({ type: "start_submit" });
      const timeout = window.setTimeout(() => controller.abort(), 22_000);
      const response = await fetch("/api/check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...request, turnstile_token: turnstileToken }),
        signal: controller.signal
      });
      window.clearTimeout(timeout);
      const parsed = await parseApiResponse(response);
      if (requestCounter.current !== requestId) return;
      dispatch({ type: "success", response: parsed });
    } catch (error) {
      if (controller.signal.aborted) {
        dispatch({
          type: "error",
          error: {
            code: "UPSTREAM_TIMEOUT",
            message: "The vibe department took too long at lunch.",
            retryable: true
          }
        });
        return;
      }
      const apiError = error as ApiError;
      if (error instanceof Error && error.message.toLowerCase().includes("turnstile")) {
        dispatch({
          type: "error",
          error: {
            code: "TURNSTILE_FAILED",
            message: "The anti-bot bouncer got suspicious. Try that once more.",
            retryable: true
          }
        });
        return;
      }
      dispatch({
        type: "error",
        error: apiError.error || {
          code: "UPSTREAM_UNAVAILABLE",
          message: "The unc detector is briefly offline. Your aura is not the issue.",
          retryable: true
        }
      });
    }
  }

  function cancel() {
    abortRef.current?.abort();
    dispatch({ type: "reset" });
  }

  function reset() {
    abortRef.current?.abort();
    dispatch({ type: "reset" });
  }

  async function adjustTone(tone: DesiredTone, getTurnstileToken: () => Promise<string>) {
    if (!originalRequestRef.current) return;
    await submit({ ...originalRequestRef.current, desired_tone: tone }, getTurnstileToken);
  }

  return {
    state,
    submit,
    adjustTone,
    cancel,
    reset
  };
}
